import React, {useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {SegmentedControl} from 'wobi-web-common/dist/v2/index.js';
import ErrorText from './ErrorText';

const ICONS = {
  heart: ['M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z'],
  plane: [
    'M17.8 19.2 16 11l3.5-3.5a2.12 2.12 0 0 0-3-3L13 8 4.8 6.2a1 1 0 0 0-.9.3l-.9.9a1 1 0 0 0 .3 1.6l4.9 2.3' +
    '-2.6 2.6H3.5a1 1 0 0 0-.7.3l-.7.7a1 1 0 0 0 .3 1.6l3 1.2 1.2 3a1 1 0 0 0 1.6.3l.7-.7a1 1 0 0 0 .3-.7v-2.1l2.6-2.6' +
    ' 2.3 4.9a1 1 0 0 0 1.6.3l.9-.9a1 1 0 0 0 .3-.9z',
  ],
  doc: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M9 13h6', 'M9 17h4'],
  stethoscope: ['M6 3v6a5 5 0 0 0 10 0V3', 'M6 3H4', 'M16 3h2', 'M12 14v3a4 4 0 0 0 8 0v-1', 'M20 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4z'],
  pill: ['M10.5 20.5 3.5 13.5a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7z', 'M8.5 8.5l7 7'],
  activity: ['M22 12h-4l-3 9L9 3l-3 9H2'],
  shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
  users: ['M16 21v-2a4 4 0 0 0-8 0v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8'],
};

const Ic = ({name, size = 22}) => {
  const paths = ICONS[name];
  if (!paths) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
};

const MedicalQuestion = ({
  icon,
  text,
  options,
  value,
  onChange,
  error,
  errorMessage,
  last,
  detail,
  detailLabel,
  disabled,
  children,
  dataTestId,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const detailId = React.useId();
  const resolvedOptions = options || [
    {value: 'yes', label: intl.formatMessage({id: 'yes'})},
    {value: 'no', label: intl.formatMessage({id: 'no'})},
  ];

  return (
    <div
      className={`medical-question${last ? ' medical-question-last' : ''}`}
    >
      <div className='medical-question-layout'>
        <div className='q-text medical-question-text'>
          {icon && (
            <span className='medical-question-icon' data-testid='medical-question-icon'>
              <Ic name={icon} size={22} />
            </span>
          )}
          <div className='medical-question-copy'>
            <div className='q-qtext medical-question-label'>
              {text}
              {detail && (
                <button
                  type='button'
                  onClick={() => setOpen((o) => !o)}
                  className='medical-detail-toggle'
                  aria-expanded={open}
                  aria-controls={detailId}
                >
                  {detailLabel || <FormattedMessage id='medical.fullDetails'/>}{' '}
                  <span className='medical-detail-chevron' data-open={open || undefined} aria-hidden='true'>
                    <svg
                      width={13}
                      height={13}
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth={2}
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M6 9l6 6 6-6' />
                    </svg>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className='q-opts medical-question-options' data-testid={dataTestId}>
          <SegmentedControl
            className='medical-answer-control'
            ariaLabel={typeof text === 'string' ? text : intl.formatMessage({id: 'medical.answerAriaLabel'})}
            value={value}
            options={resolvedOptions}
            onChange={onChange}
            invalid={error}
            disabled={disabled}
          />
          {error && <ErrorText>{errorMessage}</ErrorText>}
        </div>
        {detail && open && (
          <div id={detailId} className='q-detail medical-question-detail'>
            {detail}
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

export default MedicalQuestion;
